import { useMemo, useState } from 'react'
import { gql, useQuery } from '@apollo/client'
import { useQueryVariables } from './store'

export const LANG_LIST = ['fr', 'en', 'de', 'es']

export const OPERATION_FIND_ADVERTISEMENTS = gql`
  query Advertisements(
    $site_sf_ids: [ID]
    $verbatim: String
    $statuses: [String]
    $publish_start_at: String
    $publish_end_at: String
    $sort_by: String
    $sort_direction: String
    $offset: Int
    $limit: Int
  ) {
    advertisements: portalAdvertisements(
      site_sf_ids: $site_sf_ids
      verbatim: $verbatim
      statuses: $statuses
      publish_start_at: $publish_start_at
      publish_end_at: $publish_end_at
      sort_by: $sort_by
      sort_direction: $sort_direction
      offset: $offset
      limit: $limit
    ) {
      name
      status
      id
      starts_at
      ends_at
      creator {
        id
        first_name
        last_name
      }
      count_sites
      count_accounts
      can_be_edit
      fallback_language
    }
  }
`

const OPERATION_FIND_ADVERTISEMENT = gql`
  query Advertisement($id: ID!) {
    advertisement: portalAdvertisement(id: $id) {
      name
      status
      id
      starts_at
      ends_at
      contents {
        language
        header
        body
        footer
      }
      can_be_edit
      fallback_language
      accounts: accounts_with_parent {
        name
        hierarchie
        sf_parent_id
        sf_id
        nb_children
        is_site
      }
      image {
        dataURL
        name
        width
        height
      }
      count_sites
    }
  }
`

export const OPERATION_EXPORT_ADVERTISEMENTS = gql`
  query ExportAdvertisements(
    $site_sf_ids: [ID]
    $verbatim: String
    $status: [String]
    $publish_start_at: String
    $publish_end_at: String
    $format: String!
  ) {
    exportPortalAdvertisements(
      site_sf_ids: $site_sf_ids
      verbatim: $verbatim
      status: $status
      publish_start_at: $publish_start_at
      publish_end_at: $publish_end_at
      format: $format
    )
  }
`

export const PAGE_SIZE = 20

const useAdvertisements = () => {
  const [shouldFetchNextPage, setShouldFetchNextPage] = useState(true)
  const [page, setPage] = useState(1)
  const queryVariables = useQueryVariables()
  const { data, loading, error, fetchMore, networkStatus } = useQuery(OPERATION_FIND_ADVERTISEMENTS, {
    notifyOnNetworkStatusChange: true,
    variables: {
      ...queryVariables,
      limit: PAGE_SIZE
    }
  })

  const advertisements = useMemo(() => {
    if (!data) {
      return []
    }
    return data.advertisements
  }, [data])

  return {
    advertisements,
    error,
    loading: (loading && networkStatus !== 3) || networkStatus === 2,
    loadingNext: networkStatus === 3,
    fetchNextPage: async () => {
      if (networkStatus === 3) {
        return
      }
      const newPage = page + 1
      setPage(newPage)
      try {
        const fetchMoreResult = await fetchMore({
          variables: {
            offset: newPage,
            limit: PAGE_SIZE
          },
          errorPolicy: 'all',
          updateQuery: (prev, { fetchMoreResult }) => {
            if (!fetchMoreResult?.advertisements) return prev
            return {
              advertisements: [...prev.advertisements, ...fetchMoreResult.advertisements]
            }
          }
        })
        if (!fetchMoreResult.data?.advertisements?.length || fetchMoreResult.data.advertisements.length < PAGE_SIZE) {
          setShouldFetchNextPage(false)
        }
      } catch (e) {
        setShouldFetchNextPage(false)
      }
    },
    shouldFetchNextPage: !loading && advertisements.length >= PAGE_SIZE && shouldFetchNextPage
  }
}

export const useAdvertisement = ({ id }, options) => {
  const { data, loading, error } = useQuery(OPERATION_FIND_ADVERTISEMENT, {
    variables: { id },
    fetchPolicy: 'no-cache',
    ...options
  })
  const advertisement = useMemo(() => {
    if (!data) {
      return null
    }
    const contents = LANG_LIST.map(language => {
      const content = data.advertisement.contents.find(content => content.language === language)
      return {
        language,
        header: content?.header || '',
        body: content?.body || '',
        footer: content?.footer || ''
      }
    })
    const { accounts } = data.advertisement
    return {
      ...data.advertisement,
      site_ids: accounts.filter(({ is_site }) => is_site).map(({ sf_id }) => sf_id),
      starts_at: data.advertisement.starts_at ? new Date(data.advertisement.starts_at) : '',
      ends_at: data.advertisement.ends_at ? new Date(data.advertisement.ends_at) : '',
      contents
    }
  }, [data])

  return {
    advertisement,
    error,
    loading
  }
}

export default useAdvertisements
